import { useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import {
  PageShell,
  KpiGrid,
  KpiCard,
  FilterBar,
  FilterField,
  Pagination,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Button,
  Input,
  RadixSelect,
} from '@/components/ui';
import type { InventoryStatusEnum } from '@/lib/api-admin/types';
import { useInventoryQuery } from '../hooks/useInventoryQuery';
import { useInventoryKpiQuery } from '../hooks/useInventoryKpiQuery';
import { useInventoryHistoryQuery } from '../hooks/useInventoryHistoryQuery';
import { useInventoryLocationsQuery } from '../hooks/useInventoryLocationsQuery';
import { InventoryTable } from '../components/InventoryTable';
import { InventoryHistoryTable } from '../components/InventoryHistoryTable';
import { InventoryLocationManager } from '../components/InventoryLocationManager';
import { adminFetch } from '@/lib/apiClient';
import { ApiClientError } from '@/lib/http-core';

const STATUS_VALUES: ReadonlyArray<InventoryStatusEnum> = ['NORMAL', 'LOW', 'OUT_OF_STOCK'];

export const InventoryListPage = () => {
  const { t } = useTranslation('inventory');
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [locationId, setLocationId] = useState<number | null>(null);
  const [status, setStatus] = useState<InventoryStatusEnum | null>(null);

  const statusOptions = [
    { value: '', label: t('inventory.filters.statusAll') },
    ...STATUS_VALUES.map((value) => ({ value, label: t(`inventory.status.${value}`) })),
  ];

  const { data: kpi } = useInventoryKpiQuery();
  const { data: inventoryData, isLoading } = useInventoryQuery({
    page,
    size: 20,
    ...(keyword ? { keyword } : {}),
    ...(locationId ? { locationId } : {}),
    ...(status ? { status } : {}),
  });
  const { data: historyData, isLoading: historyLoading } = useInventoryHistoryQuery({ page: 1, size: 20 });
  const { data: locations, isLoading: locationsLoading } = useInventoryLocationsQuery();

  const locationOptions = [
    { value: '', label: t('inventory.filters.locationAll') },
    ...(locations ?? []).map((loc) => ({ value: String(loc.locationId), label: loc.name })),
  ];

  const handleCsvExport = async () => {
    // 종전: `(apiClient as unknown as { token?: string }).token` 로 토큰을 읽었는데 apiClient 에는
    // 그런 속성이 없어 항상 `Bearer ` 빈 값이 나갔고(→401), res.ok 검사도 없어 **오류 응답 본문이
    // 그대로 inventory.csv 로 저장**됐다. adminFetch 가 인증·401 갱신을 처리한다.
    const resp = await adminFetch('/admin/v1/inventory/export');
    if (!resp.ok) {
      throw new ApiClientError({ code: 'INVENTORY_EXPORT_FAILED', message: '' }, { status: resp.status });
    }
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'inventory.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleReset = () => {
    setKeyword('');
    setLocationId(null);
    setStatus(null);
    setPage(1);
  };

  const total = inventoryData?.meta?.pagination?.totalItems ?? 0;

  return (
    <PageShell
      title={t('inventory.title')}
      breadcrumb={[
        { label: t('inventory.breadcrumb.home'), href: '/dashboard' },
        { label: t('inventory.breadcrumb.products'), href: '/products' },
        { label: t('inventory.breadcrumb.inventory') },
      ]}
      actions={
        <Button size="sm" onClick={handleCsvExport}>
          {t('inventory.actions.export')}
        </Button>
      }
    >
      <KpiGrid columns={4}>
        <KpiCard label={t('inventory.kpi.totalSku')} value={kpi?.totalSku ?? '-'} />
        <KpiCard label={t('inventory.kpi.normal')} value={kpi?.normal ?? '-'} />
        <KpiCard label={t('inventory.kpi.low')} value={kpi?.low ?? '-'} />
        <KpiCard label={t('inventory.kpi.outOfStock')} value={kpi?.outOfStock ?? '-'} />
      </KpiGrid>

      <Tabs defaultValue="stock">
        <TabsList>
          <TabsTrigger value="stock">{t('inventory.tabs.stock')}</TabsTrigger>
          <TabsTrigger value="locations">{t('inventory.tabs.locations')}</TabsTrigger>
          <TabsTrigger value="history">{t('inventory.tabs.history')}</TabsTrigger>
        </TabsList>

        <TabsContent value="stock" className="flex flex-col gap-4 mt-4">
          <FilterBar
            filters={
              <>
                <FilterField label={t('inventory.filters.keyword')}>
                  <Input
                    id="inventory-keyword"
                    type="search"
                    placeholder={t('inventory.filters.keywordPlaceholder')}
                    value={keyword}
                    onChange={(e) => { setKeyword(e.target.value); setPage(1); }}
                  />
                </FilterField>
                <FilterField label={t('inventory.filters.location')}>
                  <RadixSelect
                    value={locationId ? String(locationId) : ''}
                    onChange={(v) => {
                      setLocationId(v === '' ? null : Number(v));
                      setPage(1);
                    }}
                    options={locationOptions}
                    placeholder={t('inventory.filters.locationAll')}
                  />
                </FilterField>
                <FilterField label={t('inventory.filters.status')}>
                  <RadixSelect
                    value={status ?? ''}
                    onChange={(v) => {
                      setStatus(v === '' ? null : (v as InventoryStatusEnum));
                      setPage(1);
                    }}
                    options={statusOptions}
                    placeholder={t('inventory.filters.statusAll')}
                  />
                </FilterField>
              </>
            }
            actions={
              <Button type="button" variant="ghost" size="sm" onClick={handleReset}>
                {t('inventory.actions.reset')}
              </Button>
            }
          />
          <InventoryTable items={inventoryData?.items ?? []} isLoading={isLoading} />
          {total > 0 ? (
            <Pagination
              total={total}
              currentPage={page}
              pageSize={20}
              onPageChange={setPage}
            />
          ) : null}
        </TabsContent>

        <TabsContent value="locations" className="mt-4">
          <InventoryLocationManager locations={locations ?? []} isLoading={locationsLoading} />
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <InventoryHistoryTable items={historyData?.items ?? []} isLoading={historyLoading} />
        </TabsContent>
      </Tabs>
    </PageShell>
  );
};
